import React, { useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Plus, Trash2, Save, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import './AnagramBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AnagramBuilder() {
  const { user } = React.useContext(AuthContext);
  const [gameData, setGameData] = useState({
    title: '',
    timer_setting: 'off',
    words: [{ word: '', split_type: 'letters', image_url: '', hint: '' }]
  });
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiNumWords, setAiNumWords] = useState(5);
  const [aiDifficulty, setAiDifficulty] = useState('easy');
  const [generating, setGenerating] = useState(false);
  
  const addWord = () => setGameData({ ...gameData, words: [...gameData.words, { word: '', split_type: 'letters', image_url: '', hint: '' }] });
  
  const handleImageUpload = async (file, wordIndex) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      const newWords = [...gameData.words];
      newWords[wordIndex].image_url = response.data.url;
      setGameData({ ...gameData, words: newWords });
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    }
  };
  const deleteWord = (index) => setGameData({ ...gameData, words: gameData.words.filter((_, i) => i !== index) });
  const updateWord = (index, field, value) => {
    const newWords = [...gameData.words];
    newWords[index][field] = value;
    setGameData({ ...gameData, words: newWords });
  };
  
  const saveGame = async () => {
    if (!gameData.title) return toast.error('Please enter a game title');
    if (gameData.words.some(w => !w.word)) return toast.error('Please fill all words');
    try {
      await axios.post(`${API}/anagram`, gameData, { withCredentials: true });
      toast.success('Game saved successfully!');
      setGameData({ title: '', timer_setting: 'off', words: [{ word: '', split_type: 'letters', image_url: null, hint: '' }] });
    } catch (error) {
      console.error('Failed to save game:', error);
      toast.error('Failed to save game');
    }
  };
  
  const generateAIWords = async () => {
    if (!aiTopic) return toast.error('Please enter a topic');
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-anagram`, { topic: aiTopic, num_words: aiNumWords, difficulty: aiDifficulty }, { withCredentials: true });
      toast.success('AI Words generated!');
      setAiDialogOpen(false);
      setAiTopic('');
      setGameData({ ...gameData, words: response.data.words.map(w => ({ word: w.word, split_type: 'letters', image_url: null, hint: w.hint || '' })) });
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate words');
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="anagram-builder-page">
      <div className="builder-container">
        <div className="builder-header">
          <h1>🔤 Anagram Builder</h1>
          <div className="header-actions">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild><Button className="ai-btn"><Sparkles size={20} />AI Word Generator</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>AI Word Generator</DialogTitle></DialogHeader>
                <div className="ai-form">
                  <div className="form-group"><Label>Topic</Label><Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g., Animals, Colors..." /></div>
                  <div className="form-group"><Label>Difficulty</Label>
                    <Select value={aiDifficulty} onValueChange={setAiDifficulty}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="easy">Easy (3-5 letters)</SelectItem><SelectItem value="medium">Medium (5-7 letters)</SelectItem><SelectItem value="hard">Hard (7-10 letters)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="form-group"><Label>Number of Words</Label><Input type="number" min="3" max="10" value={aiNumWords} onChange={(e) => setAiNumWords(parseInt(e.target.value))} /></div>
                  <Button onClick={generateAIWords} disabled={generating} className="generate-btn">{generating ? 'Generating...' : 'Generate Words'}</Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={saveGame} className="save-btn"><Save size={20} />Save Game</Button>
          </div>
        </div>
        <div className="game-settings">
          <Input value={gameData.title} onChange={(e) => setGameData({ ...gameData, title: e.target.value })} placeholder="Game Title" className="title-input" />
          <div className="setting-group">
            <Label>Timer</Label>
            <Select value={gameData.timer_setting} onValueChange={(value) => setGameData({ ...gameData, timer_setting: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="off">OFF</SelectItem><SelectItem value="30s">30 seconds</SelectItem><SelectItem value="1min">1 minute</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="words-section">
          <h2>Words ({gameData.words.length})</h2>
          <div className="words-list">
            {gameData.words.map((word, index) => (
              <div key={index} className="word-card">
                <div className="word-header">
                  <span>Word {index + 1}</span>
                  {gameData.words.length > 1 && <Button variant="ghost" size="sm" onClick={() => deleteWord(index)}><Trash2 size={18} /></Button>}
                </div>
                <div className="word-content">
                  <div className="form-group"><Label>Word</Label><Input value={word.word} onChange={(e) => updateWord(index, 'word', e.target.value)} placeholder="Enter word" /></div>
                  <div className="form-group"><Label>Split Type</Label>
                    <Select value={word.split_type} onValueChange={(value) => updateWord(index, 'split_type', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="letters">Letters (e.g., C-A-T)</SelectItem><SelectItem value="syllables">Syllables (e.g., EL-E-PHANT)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="form-group"><Label>Hint (optional)</Label><Input value={word.hint} onChange={(e) => updateWord(index, 'hint', e.target.value)} placeholder="Helpful hint" /></div>
                  <div className="form-group">
                    <Label>Image (optional)</Label>
                    <input type="file" accept="image/*" id={`word-img-${index}`} onChange={(e) => handleImageUpload(e.target.files[0], index)} style={{ display: 'none' }} />
                    <label htmlFor={`word-img-${index}`} className="upload-btn-sm">Upload Image</label>
                    {word.image_url && <img src={word.image_url} alt="Word" className="word-preview-img" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={addWord} className="add-word-btn"><Plus size={20} />Add Word</Button>
        </div>
      </div>
    </div>
  );
}

export default AnagramBuilder;
